import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Pocket calculator shown in a modal on the POS screen. Works like a phone
// calculator (each operator applies immediately), accepts keyboard input while
// open, and can send its result to the sale's payment amount.
export default class extends Controller {
  static targets = ["display", "expression"]

  connect() {
    this.reset()
    this.keyHandler = this.handleKey.bind(this)
    this.element.addEventListener("keydown", this.keyHandler)
    this.element.addEventListener("shown.bs.modal", () => this.element.focus())
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.keyHandler)
  }

  // Buttons ------------------------------------------------------------------

  digit(event) {
    this.enterDigit(String(event.params.digit))
  }

  decimal() {
    this.enterDecimal()
  }

  pickOperator(event) {
    this.chooseOperator(String(event.params.op))
  }

  equals() {
    this.calculate()
  }

  percent() {
    if (this.error) return
    const current = Number(this.current)
    if (this.pending && this.previous !== null) {
      this.current = this.stringify((this.previous * current) / 100)
    } else {
      this.current = this.stringify(current / 100)
    }
    this.overwrite = true
    this.render()
  }

  negate() {
    if (this.error || this.current === "0") return
    this.current = this.current.startsWith("-") ? this.current.slice(1) : `-${this.current}`
    this.render()
  }

  backspace() {
    if (this.error) return this.clear()
    if (this.overwrite) return
    this.current = this.current.length > 1 && this.current !== "-0" ? this.current.slice(0, -1) : "0"
    if (this.current === "-") this.current = "0"
    this.render()
  }

  clear() {
    this.reset()
    this.render()
  }

  // Sends the shown number to the sale's payment amount and closes the dialog.
  apply() {
    if (this.error) return
    const value = Number(this.current)
    if (!Number.isFinite(value) || value < 0) return

    this.dispatch("apply", { detail: { value } })
    Modal.getOrCreateInstance(this.element).hide()
  }

  // Keyboard -----------------------------------------------------------------

  handleKey(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return
    const key = event.key

    if (/^[0-9]$/.test(key)) this.enterDigit(key)
    else if (key === "." || key === ",") this.enterDecimal()
    else if (["+", "-", "*", "/"].includes(key)) this.chooseOperator({ "*": "×", "/": "÷" }[key] || key)
    else if (key === "Enter" || key === "=") this.calculate()
    else if (key === "Backspace") this.backspace()
    else if (key === "%") this.percent()
    else if (key.toLowerCase() === "c" || key === "Delete") this.clear()
    else return

    event.preventDefault()
  }

  // Calculator logic ---------------------------------------------------------

  reset() {
    this.current = "0"
    this.previous = null
    this.pending = null
    this.overwrite = false
    this.error = false
    this.expression = ""
  }

  enterDigit(digit) {
    if (this.error) this.reset()
    if (this.overwrite) {
      this.current = "0"
      this.overwrite = false
      if (this.pending === null) this.expression = ""
    }
    if (this.current.replace(/[-.]/g, "").length >= 15) return
    this.current = this.current === "0" ? digit : this.current === "-0" ? `-${digit}` : this.current + digit
    this.render()
  }

  enterDecimal() {
    if (this.error) this.reset()
    if (this.overwrite) {
      this.current = "0"
      this.overwrite = false
      if (this.pending === null) this.expression = ""
    }
    if (!this.current.includes(".")) this.current += "."
    this.render()
  }

  chooseOperator(op) {
    if (this.error) return
    if (this.pending && !this.overwrite) {
      this.calculate({ keepOperator: true })
      if (this.error) return
    }
    this.previous = Number(this.current)
    this.pending = op
    this.overwrite = true
    this.expression = `${this.format(this.stringify(this.previous))} ${op}`
    this.render()
  }

  calculate({ keepOperator = false } = {}) {
    if (this.error || this.pending === null || this.previous === null) return

    const left = this.previous
    const right = Number(this.current)
    let result

    switch (this.pending) {
      case "+": result = left + right; break
      case "-": result = left - right; break
      case "×": result = left * right; break
      case "÷":
        if (right === 0) { this.error = true; this.expression = ""; this.render(); return }
        result = left / right
        break
    }

    if (!keepOperator) {
      this.expression = `${this.format(this.stringify(left))} ${this.pending} ${this.format(this.stringify(right))} =`
    }

    this.current = this.stringify(result)
    this.previous = null
    if (!keepOperator) this.pending = null
    this.overwrite = true
    this.render()
  }

  // Trims floating point noise (0.1 + 0.2 => 0.3).
  stringify(number) {
    return String(Number.parseFloat(number.toPrecision(12)))
  }

  format(value) {
    const [whole, fraction] = value.split(".")
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return fraction !== undefined ? `${grouped}.${fraction}` : grouped
  }

  render() {
    this.displayTarget.textContent = this.error ? "Cannot divide by 0" : this.format(this.current)
    this.expressionTarget.textContent = this.expression || " "
  }
}
